import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { newsService } from '../../../services/newsService';
import { userCompanyService } from '../CompaniesPage/service/userCompany.service';
import { companyService } from '../CompaniesPage/service/company.service';
import { NewsItem, DashboardStats } from '../../../types';
import { useUserData } from '../../../hooks/useUserData';
import { getUserPlan, hasPremiumSearchAccess } from '../../../utils/userPlan';
import { useNavigate } from 'react-router-dom';

const CHART_RANGE_OPTIONS = ['1d', '5d', '1mo', '3mo', '6mo', '1y', '5y', '10y'] as const;
type ChartRangeOption = (typeof CHART_RANGE_OPTIONS)[number];
const FACTS_PAGE_SIZE = 10;
type DashboardCompany = { id: string; nome: string; ticker?: string; tickers: string[] };

const getChartRangeBounds = (range: ChartRangeOption): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date(to);

  switch (range) {
    case '1d':
      from.setDate(to.getDate() - 1);
      break;
    case '5d':
      from.setDate(to.getDate() - 5);
      break;
    case '1mo':
      from.setMonth(to.getMonth() - 1);
      break;
    case '3mo':
      from.setMonth(to.getMonth() - 3);
      break;
    case '6mo':
      from.setMonth(to.getMonth() - 6);
      break;
    case '1y':
      from.setFullYear(to.getFullYear() - 1);
      break;
    case '5y':
      from.setFullYear(to.getFullYear() - 5);
      break;
    case '10y':
      from.setFullYear(to.getFullYear() - 10);
      break;
    default:
      from.setMonth(to.getMonth() - 1);
  }

  return { from, to };
};

const Dashboard: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useUserData();
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSubscriptions, setHasSubscriptions] = useState(true);
  const [subscribedCompanies, setSubscribedCompanies] = useState<DashboardCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedTicker, setSelectedTicker] = useState<string>('');
  const [companyMapById, setCompanyMapById] = useState<Map<string, DashboardCompany>>(new Map());
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<'daily' | 'premium'>('daily');
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<NewsItem[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastSearchText, setLastSearchText] = useState('');
  const [totalNewsCount, setTotalNewsCount] = useState(0);
  const [factsPage, setFactsPage] = useState(1);
  const [selectedChartRange, setSelectedChartRange] = useState<ChartRangeOption>('1mo');
  const [isNewsPriceChartLoading, setIsNewsPriceChartLoading] = useState(false);
  const [priceHistory, setPriceHistory] = useState<Array<{ data: string; fechamento: number }>>([]);
  const [priceChartCurrency, setPriceChartCurrency] = useState('BRL');
  const isCompanySelected = selectedCompanyId !== '';

  const selectedCompanyName = useMemo(() => {
    if (!selectedCompanyId) {
      return '';
    }
    return subscribedCompanies.find(company => company.id === selectedCompanyId)?.nome || '';
  }, [selectedCompanyId, subscribedCompanies]);

  const filteredCompanies = useMemo(() => {
    const normalizedSearch = companySearchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return subscribedCompanies.slice(0, 5);
    }

    return subscribedCompanies
      .filter(company =>
        company.nome.toLowerCase().includes(normalizedSearch) ||
        (company.ticker || '').toLowerCase().includes(normalizedSearch) ||
        company.tickers.some((ticker) => ticker.toLowerCase().includes(normalizedSearch))
      );
  }, [companySearchTerm, subscribedCompanies]);

  const selectedCompanyTicker = useMemo(() => {
    if (!isCompanySelected) return '';
    const selectedCompany = companyMapById.get(selectedCompanyId);
    const fallbackTicker = selectedCompany?.ticker || selectedCompany?.tickers?.[0] || '';
    const rawTicker = selectedTicker || fallbackTicker;
    return rawTicker.trim().toUpperCase();
  }, [companyMapById, isCompanySelected, selectedCompanyId, selectedTicker]);

  const selectedCompanyTickers = useMemo(() => {
    if (!isCompanySelected) return [];
    return companyMapById.get(selectedCompanyId)?.tickers || [];
  }, [companyMapById, isCompanySelected, selectedCompanyId]);

  const totalFactsPages = useMemo(
    () => Math.max(1, Math.ceil(newsItems.length / FACTS_PAGE_SIZE)),
    [newsItems.length]
  );

  const paginatedNewsItems = useMemo(() => {
    const start = (factsPage - 1) * FACTS_PAGE_SIZE;
    return newsItems.slice(start, start + FACTS_PAGE_SIZE);
  }, [newsItems, factsPage]);

  // Determina o plano do usuário e acesso à busca premium
  const userPlan = user ? getUserPlan(user.groups) : null;
  const hasPremiumAccess = hasPremiumSearchAccess(userPlan);

  useEffect(() => {
    if (isAuthenticated) {
      loadSubscribedCompanies();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setCompanySearchTerm(selectedCompanyName);
  }, [selectedCompanyName]);

  useEffect(() => {
    setSelectedTicker('');
  }, [selectedCompanyId]);

  useEffect(() => {
    setFactsPage(1);
  }, [selectedCompanyId, selectedChartRange, newsItems.length]);

  useEffect(() => {
    const loadNewsAndPriceData = async () => {
      if (!isAuthenticated || !selectedCompanyTicker) {
        setPriceHistory([]);
        setNewsItems([]);
        setTotalNewsCount(0);
        setStats({
          totalNews: 0,
          positiveNews: 0,
          negativeNews: 0,
          neutralNews: 0,
          unreadCount: 0,
        });
        return;
      }

      try {
        setIsNewsPriceChartLoading(true);
        const { from, to } = getChartRangeBounds(selectedChartRange);
        const [companyData, newsData] = await Promise.all([
          companyService.getTickerPriceData(selectedCompanyTicker, selectedChartRange),
          newsService.getNews({
            empresa: selectedCompanyId,
            dataInicio: from.toISOString(),
            dataFim: to.toISOString(),
          }),
        ]);

        const normalizedHistory = (companyData?.historicalDataPrice || [])
          .filter((point) => typeof point.fechamento === 'number')
          .map((point) => ({ data: point.data, fechamento: point.fechamento }))
          .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());

        setPriceHistory(normalizedHistory);
        setPriceChartCurrency(companyData?.moeda || 'BRL');

        setNewsItems(newsData);
        setTotalNewsCount(newsData.length);

        const totalNews = newsData.length;
        const positiveNews = newsData.filter(news => news.impact === 'positive').length;
        const negativeNews = newsData.filter(news => news.impact === 'negative').length;
        const neutralNews = newsData.filter(news => news.impact === 'neutral').length;
        const unreadCount = newsData.filter(news => !news.isRead).length;
        setStats({
          totalNews,
          positiveNews,
          negativeNews,
          neutralNews,
          unreadCount,
        });
      } catch (error) {
        console.error('Erro ao carregar dados do gráfico notícia x preço:', error);
        setPriceHistory([]);
        setNewsItems([]);
        setTotalNewsCount(0);
      } finally {
        setIsNewsPriceChartLoading(false);
      }
    };

    loadNewsAndPriceData();
  }, [isAuthenticated, selectedCompanyId, selectedCompanyTicker, selectedChartRange]);

  const loadSubscribedCompanies = async () => {
    try {
      setIsLoading(true);
      const [userSubs, allCompanies] = await Promise.all([
        userCompanyService.getUserCompanies(),
        companyService.getAllCompanies(),
      ]);
      // Mapear empresas do usuário para obter IDs
      const subscribedCompanyNames = new Set(
        userSubs
          .map((sub: any) => sub.company)
          .filter((company: string | undefined | null) => Boolean(company))
      );

      // Criar lista de empresas com ID e nome
      const userCompanies = allCompanies
        .filter(company => subscribedCompanyNames.has(company.nome))
        .map(company => {
          const tickers = Array.from(
            new Set(
              [company.ticker, ...(Array.isArray(company.tickers) ? company.tickers : [])]
                .filter((ticker): ticker is string => Boolean(ticker && ticker.trim()))
                .map((ticker) => ticker.trim().toUpperCase())
            )
          );

          return {
            id: company.id,
            nome: company.nome,
            ticker: tickers[0],
            tickers,
          };
        });

      // Criar mapa para facilitar busca
      const companyMapById = new Map(userCompanies.map(c => [c.id, c]));

      setSubscribedCompanies(userCompanies);
      setHasSubscriptions(userCompanies.length > 0);
      setCompanyMapById(companyMapById);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const goToAnalysis = (item: NewsItem) => {
    const analysisKey = encodeURIComponent(item.analysisId || item.id);
    navigate(`/dashboard/analise/${analysisKey}`, {
      state: { analysis: item },
    });
  };

  const handleNewsPointClick = (params: any) => {
    if (params?.seriesName !== 'Notícias') return;

    const analysisKey = params?.data?.analysisKey;
    if (!analysisKey) return;

    const detailUrl = `${window.location.origin}/dashboard/analise/${encodeURIComponent(String(analysisKey))}`;
    window.open(detailUrl, '_blank', 'noopener,noreferrer');
  };

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'positive': return '📈';
      case 'negative': return '📉';
      default: return '➡️';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'positive': return 'var(--success-green)';
      case 'negative': return 'var(--danger-red)';
      default: return 'var(--neutral-gray)';
    }
  };

  const formatNewsTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const handlePremiumSearch = async () => {
    const trimmedText = searchText.trim();
    if (!trimmedText) {
      setSearchError('Digite um termo para buscar.');
      return;
    }

    setIsSearchLoading(true);
    setSearchError(null);
    setLastSearchText(trimmedText);
    setSearchResults([]);
    const results = await newsService.searchNewsByContext(trimmedText);
    setSearchResults(results);
    setIsSearchLoading(false);
  };

  const parseToDate = (value: string | number) => {
    if (typeof value === 'number') {
      const normalized = value < 1_000_000_000_000 ? value * 1000 : value;
      const parsedNumber = new Date(normalized);
      return Number.isNaN(parsedNumber.getTime()) ? null : parsedNumber;
    }

    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      const numericValue = Number(trimmed);
      const normalized = numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue;
      const parsedNumericString = new Date(normalized);
      if (!Number.isNaN(parsedNumericString.getTime())) {
        return parsedNumericString;
      }
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatMoneyShort = (value: number, currency = 'BRL') =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);

  const newsVsPriceChartOptions: EChartsOption = useMemo(() => {
    if (priceHistory.length === 0) {
      return {};
    }

    const lineData = priceHistory
      .map((point) => {
        const date = parseToDate(point.data);
        return date ? [date.getTime(), point.fechamento] : null;
      })
      .filter(Boolean) as Array<[number, number]>;

    if (lineData.length === 0) {
      return {};
    }

    const newsMarkers = newsItems
      .flatMap((item) => {
        const relatedNews = item.analysisNews && item.analysisNews.length > 0
          ? item.analysisNews
          : [{ time: item.time, title: item.title, url: item.url || item.id }];

        return relatedNews.map((news) => {
          const publishedAt = parseToDate(news.time || '');
          if (!publishedAt || lineData.length === 0) return null;
          const publishedAtTs = publishedAt.getTime();

          let closestPoint = lineData[0];
          let smallestDiff = Math.abs(lineData[0][0] - publishedAtTs);
          for (let i = 1; i < lineData.length; i += 1) {
            const diff = Math.abs(lineData[i][0] - publishedAtTs);
            if (diff < smallestDiff) {
              smallestDiff = diff;
              closestPoint = lineData[i];
            }
          }

          return {
            // X usa o instante real da notícia; Y usa o preço mais próximo do histórico.
            value: [publishedAtTs, closestPoint[1]],
            newsTitle: item.fact || news.title,
            impact: item.impact,
            analysisKey: item.analysisId || item.id,
          };
        });
      })
      .filter(Boolean) as Array<{
      value: [number, number];
      newsTitle: string;
      impact: string;
      analysisKey: string;
    }>;

    return {
      grid: { left: 12, right: 12, top: 24, bottom: 36, containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#111827',
        borderWidth: 0,
        textStyle: { color: '#f9fafb' },
        formatter: (params: any) => {
          const rows = Array.isArray(params) ? params : [params];
          const first = rows[0];
          if (!first) return '';

          const headerDate = new Date(first.value[0]).toLocaleDateString('pt-BR');
          const newsRows = rows.filter((row: any) => row.seriesName === 'Notícias');

          return `
            <div style="min-width:220px">
              <div style="font-weight:700;margin-bottom:8px">${headerDate}</div>
              ${
                newsRows.length > 0
                  ? `<div style="margin-top:2px;font-weight:600">Fato(s):</div>
                     ${newsRows.map((row: any) => `<div style="margin-top:4px">• ${row.data.newsTitle}</div>`).join('')}`
                  : '<div>Sem notícia neste ponto.</div>'
              }
            </div>
          `;
        },
      },
      xAxis: {
        type: 'time',
        axisLabel: { color: '#6b7280' },
        axisLine: { lineStyle: { color: '#d1d5db' } },
      },
      yAxis: {
        type: 'value',
        scale: true,
        axisLabel: {
          color: '#6b7280',
          formatter: (value: number) => formatMoneyShort(value, priceChartCurrency),
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 4, borderColor: '#e5e7eb' }],
      series: [
        {
          name: 'Preço',
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 3, color: '#1d4ed8' },
          areaStyle: { color: 'rgba(29, 78, 216, 0.12)' },
          data: lineData,
        },
        {
          name: 'Notícias',
          type: 'scatter',
          symbolSize: 10,
          itemStyle: {
            color: (params: any) => {
              const impact = params.data?.impact;
              if (impact === 'positive') return '#16a34a';
              if (impact === 'negative') return '#dc2626';
              return '#6b7280';
            },
            borderColor: '#ffffff',
            borderWidth: 1.5,
          },
          data: newsMarkers,
          z: 5,
        },
      ],
      animationDuration: 350,
    };
  }, [newsItems, priceChartCurrency, priceHistory]);

  if (isAuthLoading || (isAuthenticated && isLoading)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-1/2 gap-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
        <p>Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-4">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-base md:text-lg text-gray-600">Hoje, {stats?.totalNews || 0} notícias impactam suas ações.</p>
      </div>

      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 mb-6 md:mb-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Filtrar por empresa</label>
            <div className="relative">
              <input
                type="text"
                value={companySearchTerm}
                onFocus={() => setIsCompanyDropdownOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => setIsCompanyDropdownOpen(false), 120);
                }}
                onChange={(event) => {
                  setCompanySearchTerm(event.target.value);
                  setIsCompanyDropdownOpen(true);
                }}
                placeholder="Digite para buscar empresa..."
                className="w-full rounded-lg border border-gray-200 px-4 py-2 pr-24 text-sm text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => {
                  setSelectedCompanyId('');
                  setCompanySearchTerm('');
                  setIsCompanyDropdownOpen(false);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-semibold text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
              >
                Limpar
              </button>

              {isCompanyDropdownOpen && (
                <div className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map(company => (
                      <button
                        key={company.id}
                        type="button"
                        onMouseDown={() => {
                          setSelectedCompanyId(company.id);
                          setCompanySearchTerm(company.nome);
                          setIsCompanyDropdownOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        {company.nome}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">Nenhuma empresa encontrada.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="w-full lg:w-64">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Filtrar por ticker</label>
            <select
              value={selectedTicker}
              onChange={(event) => setSelectedTicker(event.target.value)}
              disabled={!isCompanySelected}
              className={`w-full rounded-lg border px-4 py-2 text-sm outline-none transition-all ${
                isCompanySelected
                  ? 'border-gray-200 text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  : 'border-gray-100 bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <option value="">
                {isCompanySelected ? (selectedCompanyTicker || 'Sem ticker disponível') : 'Selecione a empresa primeiro'}
              </option>
              {selectedCompanyTickers.map((tickerOption) => (
                <option key={tickerOption} value={tickerOption}>
                  {tickerOption}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 flex items-center gap-3 md:gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="text-2xl md:text-3xl w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center bg-green-100">📈</div>
          <div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-1">Positivas</h3>
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.positiveNews || 0}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 flex items-center gap-3 md:gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
          <div className="text-2xl md:text-3xl w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center bg-red-100">📉</div>
          <div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-1">Negativas</h3>
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.negativeNews || 0}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 flex items-center gap-3 md:gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md sm:col-span-2 lg:col-span-1">
          <div className="text-2xl md:text-3xl w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center bg-gray-100">➡️</div>
          <div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-1">Neutras</h3>
            <span className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.neutralNews || 0}</span>
          </div>
        </div>
      </div>

      <div className="mb-4 md:mb-6 flex flex-wrap gap-2 md:gap-3 items-center">
        <button
          type="button"
          onClick={() => setActiveSection('daily')}
          className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-semibold border transition-colors ${
            activeSection === 'daily'
              ? 'bg-blue-700 text-white border-blue-700'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          Notícias do dia
        </button>
        <div className="relative">
          {!hasPremiumAccess && (
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[10px] md:text-xs font-semibold text-amber-600 pointer-events-none whitespace-nowrap">
              <svg className="w-3 h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span>Assine o Pro</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              if (hasPremiumAccess) {
                setActiveSection('premium');
              } else {
                navigate('/assinatura');
              }
            }}
            disabled={!hasPremiumAccess}
            className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-semibold border transition-colors ${
              !hasPremiumAccess
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-60'
                : activeSection === 'premium'
                ? 'bg-blue-700 text-white border-blue-700'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
            title={!hasPremiumAccess ? 'Assine o plano Pro para acessar a busca premium' : ''}
          >
            Busca premium
            {!hasPremiumAccess && <span className="ml-1 text-xs">🔒</span>}
          </button>
        </div>
      </div>

      {activeSection === 'daily' && (
        <div className="news-list">
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900">Gráfico notícia x preço</h2>
              <div className="flex flex-wrap gap-2">
                {CHART_RANGE_OPTIONS.map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setSelectedChartRange(range)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                      selectedChartRange === range
                        ? 'bg-blue-700 border-blue-700 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>

            {!isCompanySelected ? (
              <p className="text-sm text-gray-600">Selecione uma empresa para visualizar o gráfico.</p>
            ) : isNewsPriceChartLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
              </div>
            ) : priceHistory.length > 0 ? (
              <>
                <ReactECharts
                  option={newsVsPriceChartOptions}
                  style={{ height: 360, width: '100%' }}
                  notMerge
                  lazyUpdate
                  onEvents={{ click: handleNewsPointClick }}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Pontos indicam o momento da publicação das notícias da listagem atual.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-600">Sem histórico disponível para a empresa/período selecionado.</p>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Notícias do dia</h2>
            {isCompanySelected && (
              <div className="text-xs md:text-sm text-gray-600">
                Total de análises: <strong>{totalNewsCount}</strong>
              </div>
            )}
          </div>
        {!isCompanySelected ? (
          <div className="text-center py-16 px-8 bg-white rounded-xl border border-gray-200">
            <h3 className="text-2xl text-gray-900 mb-2">Selecione uma empresa</h3>
            <p className="text-gray-600">Escolha uma empresa no filtro para visualizar notícias e o gráfico notícia x preço.</p>
          </div>
        ) : !hasSubscriptions ? (
          <div className="text-center py-16 px-8 bg-white rounded-xl border border-gray-200">
            <h3 className="text-2xl text-gray-900 mb-2">Nenhuma empresa monitorada</h3>
            <p className="text-gray-600">Adicione empresas na sua lista para ver notícias no dashboard.</p>
          </div>
        ) : newsItems.length > 0 ? (
          paginatedNewsItems.map(item => (
            <div
              key={item.id}
              className={`bg-white rounded-xl p-4 md:p-6 mb-4 shadow-sm border border-gray-200 cursor-pointer transition-all duration-200 flex flex-col sm:flex-row gap-3 md:gap-4
                ${item.isRead ? 'opacity-70' : 'border-l-4 border-blue-700'}`}
              onClick={() => goToAnalysis(item)}
            >
              <div className="flex-shrink-0 self-start">
                <span
                  className="text-2xl w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
                  style={{ color: getImpactColor(item.impact) }}
                >
                  {getImpactIcon(item.impact)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 leading-tight flex-1">
                    {item.fact || item.title}
                  </h3>
                  <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">{formatNewsTime(item.time)}</span>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fato</p>
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed">{item.fact || 'Fato não informado.'}</p>
                  </div>
                  <div className="bg-blue-50/60 rounded-lg border border-blue-100 p-3">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Análise</p>
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed">{item.summary}</p>
                  </div>
                  {item.analysisNews && item.analysisNews.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-100 p-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Notícias relacionadas
                      </p>
                      <ul className="space-y-2">
                        {item.analysisNews.slice(0, 3).map((news, index) => (
                          <li key={`${news.url}-${index}`} className="text-xs md:text-sm text-gray-700">
                            <a
                              href={news.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="hover:text-blue-700 hover:underline"
                            >
                              {news.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                  <span className="text-blue-700 font-medium text-xs md:text-sm">{item.company}</span>
                  <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-semibold uppercase
                    ${item.impact === 'positive' ? 'bg-green-100 text-green-500' :
                    item.impact === 'negative' ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-600'}`}>
                    {item.impact === 'positive'
                      ? 'Positivo'
                      : item.impact === 'negative'
                      ? 'Negativo'
                      : 'Neutro'}
                  </span>
                  {(item.url || item.id) && (
                    <a
                      href={item.url || item.id}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="text-xs md:text-sm font-semibold text-blue-700 hover:text-blue-800"
                    >
                      Ver notícia
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-16 px-8 bg-white rounded-xl border border-gray-200">
            <h3 className="text-2xl text-gray-900 mb-2">Nenhuma notícia encontrada</h3>
            <p className="text-gray-600">Ainda não há notícias para as empresas que você monitora.</p>
          </div>
        )}

        {isCompanySelected && newsItems.length > FACTS_PAGE_SIZE && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setFactsPage((prev) => Math.max(1, prev - 1))}
              disabled={factsPage === 1}
              className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                factsPage === 1
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página <strong>{factsPage}</strong> de <strong>{totalFactsPages}</strong>
            </span>
            <button
              type="button"
              onClick={() => setFactsPage((prev) => Math.min(totalFactsPages, prev + 1))}
              disabled={factsPage >= totalFactsPages}
              className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                factsPage >= totalFactsPages
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Próxima
            </button>
          </div>
        )}

        </div>
      )}

      {activeSection === 'premium' && (
        <div className="mt-2">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">Busca premium</h2>
          {!hasPremiumAccess ? (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 md:p-12 border-2 border-gray-300 text-center">
              <div className="mb-6">
                <div className="inline-block p-4 bg-gray-200 rounded-full mb-4">
                  <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Busca Premium Disponível no Plano Pro</h3>
                <p className="text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                  Faça upgrade para o plano Pro e tenha acesso à busca contextual avançada, 
                  permitindo pesquisas complexas que a IA entende o contexto completo.
                </p>
                <button
                  onClick={() => navigate('/assinatura')}
                  className="px-8 py-3 rounded-lg font-bold text-base md:text-lg text-white bg-blue-700 hover:bg-blue-800 transition-all shadow-lg hover:scale-105"
                >
                  Assine o plano Pro
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 mb-4 md:mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Termo de busca</label>
                <div className="flex flex-col md:flex-row gap-3">
                  <input
                    type="text"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Ex: grande venda de criptomoedas"
                    className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={handlePremiumSearch}
                    className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 transition-colors"
                  >
                    Buscar
                  </button>
                </div>
                {searchError && <p className="text-sm text-red-600 mt-3">{searchError}</p>}
              </div>

          {isSearchLoading ? (
            <div className="flex flex-col items-center justify-center min-h-screen-1/4 gap-4">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
              <p>Buscando notícias...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="flex flex-col gap-4">
              {searchResults.map((item, index) => (
                <div
                  key={`${lastSearchText}-${item.id}-${index}`}
                  className={`bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-200 transition-all duration-200 flex flex-col sm:flex-row gap-3 md:gap-4
                    ${item.isRead ? 'opacity-70' : 'border-l-4 border-blue-700'}`}
                >
                  <div className="flex-shrink-0 self-start">
                    <span
                      className="text-2xl w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"
                      style={{ color: getImpactColor(item.impact) }}
                    >
                      {getImpactIcon(item.impact)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 leading-tight flex-1">
                        {item.fact || item.title}
                      </h3>
                      <span className="text-xs md:text-sm text-gray-600 whitespace-nowrap">{formatNewsTime(item.time)}</span>
                    </div>
                    <div className="space-y-3 mb-4">
                      <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fato</p>
                        <p className="text-xs md:text-sm text-gray-700 leading-relaxed">{item.fact || 'Fato não informado.'}</p>
                      </div>
                      <div className="bg-blue-50/60 rounded-lg border border-blue-100 p-3">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Análise</p>
                        <p className="text-xs md:text-sm text-gray-700 leading-relaxed">{item.summary}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                      <span className="text-blue-700 font-medium text-xs md:text-sm">{item.company}</span>
                      <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-semibold uppercase
                        ${item.impact === 'positive' ? 'bg-green-100 text-green-500' :
                        item.impact === 'negative' ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-600'}`}>
                        {item.impact === 'positive'
                          ? 'Positivo'
                          : item.impact === 'negative'
                          ? 'Negativo'
                          : 'Neutro'}
                      </span>
                      {(item.url || item.id) && (
                        <a
                          href={item.url || item.id}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs md:text-sm font-semibold text-blue-700 hover:text-blue-800"
                        >
                          Ver notícia
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 px-8 bg-white rounded-xl border border-gray-200">
              <h3 className="text-xl text-gray-900 mb-2">Nenhuma notícia encontrada</h3>
              <p className="text-gray-600">Faça uma busca para ver resultados relevantes.</p>
            </div>
          )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;




